import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { backend, demoMode, isDemoLogin, startDemo, takeDemoStart, useApp } from '../data/store';
import { Icon } from '../ui/icons';
import { CodeInput } from '../ui/CodeInput';
import { editingBlocked } from './DeviceGate';

/** Where masjids register (the Noor MS website). */
export const REGISTER_URL = 'https://www.noormsystems.co.uk/register';

export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useApp((s) => s.session);
  const loc = useLocation();
  if (!session) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return <>{children}</>;
}

export function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const setSession = useApp((s) => s.setSession);
  const sandbox = backend.kind === 'sandbox' && !demoMode;
  const [email, setEmail] = useState(sandbox ? 'owner@sandbox.local' : '');
  const [password, setPassword] = useState(sandbox ? 'sandbox' : '');
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const link = backend.authLink?.() ?? { setPassword: false, error: '' };
  const session = useApp((s) => s.session);
  const [error, setError] = useState(link.error);
  // Opened from a reset or invite email: choose the password first. An expired link goes straight to asking for a new one.
  // A masjid signs in with the 5-digit code from Noor MS; team members (and the demo) with an email and password.
  const canCode = !!backend.redeemCode;
  const [mode, setMode] = useState<'code' | 'resend' | 'resent' | 'in' | 'reset' | 'sent' | 'newpw' | 'pwdone'>(() => (link.setPassword && backend.completePasswordReset ? 'newpw' : link.error ? 'reset' : canCode ? 'code' : 'in'));
  const [code, setCode] = useState('');
  const [resendNote, setResendNote] = useState('');

  // Just switched to the demo: sign in to it.
  useEffect(() => {
    if (!takeDemoStart()) return;
    void backend.signIn('demo@demo.noorview', 'demo').then((s) => { setSession(s); nav('/', { replace: true }); });
  }, [nav, setSession]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    // The demo login never reaches Supabase: it reloads this tab into the in-browser demo.
    if (mode === 'in' && backend.kind === 'supabase' && isDemoLogin(email, password)) { setBusy(true); startDemo(); return; }
    setBusy(true); setError('');
    try {
      if (mode === 'code') { await redeem(code); return; }
      if (mode === 'resend') { setResendNote(await backend.resendCode!(email)); setMode('resent'); return; }
      if (mode === 'reset') { await backend.requestPasswordReset(email); setMode('sent'); return; }
      if (mode === 'newpw') {
        await backend.completePasswordReset!(password);
        // On a phone the dashboard can't open here: the person signs in in the NoorView app instead.
        if (editingBlocked()) { backend.clearAuthLink?.(); await backend.signOut(); setSession(null); setMode('pwdone'); return; }
        setSession(backend.session()); nav('/', { replace: true }); return;
      }
      const s = await backend.signIn(email, password);
      setSession(s);
      nav((loc.state as { from?: string } | null)?.from ?? '/', { replace: true });
    } catch (x) {
      setError(x instanceof Error ? x.message : 'Couldn’t sign in.');
    } finally { setBusy(false); }
  };

  const redeem = async (c: string) => {
    if (c.length !== 5) return;
    setBusy(true); setError('');
    try {
      const s = await backend.redeemCode!(c);
      setSession(s);
      await useApp.getState().reload();
      nav('/', { replace: true });
    } catch (x) { setError(x instanceof Error ? x.message : 'That code didn’t work.'); setCode(''); }
    finally { setBusy(false); }
  };

  // Already signed in (and not here to choose a password): go to the dashboard.
  if (session && mode !== 'newpw') return <Navigate to={(loc.state as { from?: string } | null)?.from ?? '/'} replace />;

  const back = async () => {
    setError('');
    if (mode === 'newpw') { backend.clearAuthLink?.(); await backend.signOut(); setSession(null); }
    setMode(mode === 'in' ? 'reset' : 'in');
  };
  const go = (m: typeof mode) => { setError(''); setMode(m); };

  if (mode === 'pwdone') {
    return (
      <div className="login">
        <div className="ambient" aria-hidden><i /><i /><i /></div>
        <div className="login-card glass" role="status">
          <div className="brand"><img src="/noorview-logo.png" alt="" /><span><b>Noor</b>View</span></div>
          <div>
            <h1>Password saved</h1>
            <p className="hint">Open the NoorView app and sign in with your email and your new password. On a computer, you can also sign in at {location.host}.</p>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'code' || mode === 'resend' || mode === 'resent') {
    return (
      <div className="login">
        <div className="ambient" aria-hidden><i /><i /><i /></div>
        <form className="login-card glass" onSubmit={submit} noValidate>
          <div className="brand"><img src="/noorview-logo.png" alt="" /><span><b>Noor</b>View</span></div>
          <div>
            <h1>{mode === 'code' ? 'Enter Masjid Code' : 'Enter Masjid Email'}</h1>
            <p className="hint">{mode === 'code' ? 'The 5-digit code Noor MS sent your masjid.' : mode === 'resend' ? 'We’ll send a new code to your masjid’s email.' : resendNote}</p>
          </div>
          {mode === 'code' && <CodeInput value={code} onChange={(v) => { setCode(v); if (error) setError(''); }} onComplete={(v) => void redeem(v)} disabled={busy} bad={!!error} autoFocus />}
          {mode === 'resend' && (
            <label className="field"><span>Masjid email</span>
              <input className="input" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </label>
          )}
          {error && <div className="banner bad" role="alert">{error}</div>}
          {mode === 'code' && <button className="btn primary lg block" disabled={busy || code.length !== 5}>{busy ? 'Checking…' : 'Continue'}</button>}
          {mode === 'resend' && <button className="btn primary lg block" disabled={busy || !email.includes('@')}>{busy ? 'Sending…' : 'Resend Code'}</button>}
          {mode === 'resent' && <button type="button" className="btn primary lg block" onClick={() => go('code')}>Enter Code</button>}
          <div className="login-links">
            {mode === 'code' ? <button type="button" className="link" onClick={() => go('resend')}>Resend Code</button> : <button type="button" className="link" onClick={() => go('code')}>Back to code</button>}
            <button type="button" className="link" onClick={() => go('in')}>Sign in with email</button>
          </div>
          <a className="btn plain block" href={REGISTER_URL} target="_blank" rel="noopener">Register your Masjid</a>
        </form>
      </div>
    );
  }

  return (
    <div className="login">
      <div className="ambient" aria-hidden><i /><i /><i /></div>
      <form className="login-card glass" onSubmit={submit} noValidate>
        <div className="brand"><img src="/noorview-logo.png" alt="" /><span><b>Noor</b>View</span></div>
        <div>
          <h1>{mode === 'in' ? 'Sign in' : mode === 'newpw' ? 'Choose a new password' : 'Reset your password'}</h1>
          <p className="hint">{mode === 'in' ? 'Manage your masjid’s screens.' : mode === 'reset' ? 'We’ll email you a link to choose a new password.' : mode === 'newpw' ? 'You’ll use it with your email to sign in from now on.' : 'If that email has an account, a reset link is on its way.'}</p>
        </div>
        {sandbox && mode === 'in' && <div className="banner info">Sandbox mode: everything stays in this browser. Use any email and password.</div>}
        {mode !== 'sent' && mode !== 'newpw' && (
          <label className="field"><span>{mode === 'in' ? 'Email or username' : 'Email'}</span>
            <input className="input" type={mode === 'in' ? 'text' : 'email'} autoComplete="username" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </label>
        )}
        {(mode === 'in' || mode === 'newpw') && (
          <label className="field"><span>{mode === 'newpw' ? 'New password (at least 10 characters)' : 'Password'}</span>
            <span className="pw-wrap">
              <input className="input" type={showPw ? 'text' : 'password'} autoComplete={mode === 'newpw' ? 'new-password' : 'current-password'} value={password} minLength={mode === 'newpw' ? 10 : undefined} onChange={(e) => setPassword(e.target.value)} required spellCheck={false} autoCapitalize="off" />
              <button type="button" className="pw-eye" aria-label={showPw ? 'Hide password' : 'Show password'} aria-pressed={showPw} title={showPw ? 'Hide password' : 'Show password'} onClick={() => setShowPw((v) => !v)}><Icon name={showPw ? 'eyeOff' : 'eye'} size={20} /></button>
            </span>
          </label>
        )}
        {error && <div className="banner bad" role="alert">{error}</div>}
        {mode !== 'sent' && <button className="btn primary lg block" disabled={busy}>{busy ? 'Please wait…' : mode === 'in' ? 'Sign in' : mode === 'newpw' ? 'Save password' : 'Send reset link'}</button>}
        <button type="button" className="link" onClick={back}>{mode === 'in' ? 'Forgotten your password?' : 'Back to sign in'}</button>
        {canCode && mode === 'in' && <button type="button" className="link" onClick={() => go('code')}>Have a masjid code? Enter it</button>}
      </form>
    </div>
  );
}
